import { useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card/Card';
import type { Tool } from '@/features/traceability/type/Tool';
import {
  aggregateBoxPlotByReference,
  getTotalDuration,
  getTotalPieceNumber,
  getTotalVolumeRemoved,
  getAverageMalround,
} from './aggregateBoxPlotByReference';
import { DistributionChart } from './DistributionChart';

type ToolsReferencesChartsProps = {
  tools: Tool[];
  selectedReferences: string[];
  isLoading: boolean;
};

export function ToolsReferencesCharts({
  tools,
  selectedReferences,
  isLoading,
}: ToolsReferencesChartsProps) {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();

  // Calculate box plot data for each metric, with reference resolved to alias label
  const durationData = useMemo(
    () => aggregateBoxPlotByReference(tools, selectedReferences, getTotalDuration)
      .map((item) => ({ ...item, reference: resolve(item.reference).label })),
    [tools, selectedReferences, resolve]
  );

  const pieceData = useMemo(
    () => aggregateBoxPlotByReference(tools, selectedReferences, getTotalPieceNumber)
      .map((item) => ({ ...item, reference: resolve(item.reference).label })),
    [tools, selectedReferences, resolve]
  );

  const volumeData = useMemo(
    () => aggregateBoxPlotByReference(tools, selectedReferences, getTotalVolumeRemoved)
      .map((item) => ({ ...item, reference: resolve(item.reference).label })),
    [tools, selectedReferences, resolve]
  );

  const malroundData = useMemo(
    () => aggregateBoxPlotByReference(tools, selectedReferences, getAverageMalround)
      .map((item) => ({ ...item, reference: resolve(item.reference).label })),
    [tools, selectedReferences, resolve]
  );

  // Don't render if no data
  if (selectedReferences.length === 0 || tools.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="section-title mb-2">
          {t('analytics.comparison.distribution.title')}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t('analytics.comparison.distribution.description')}
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} padding="sm" className="h-[350px]">
              <Skeleton className="h-6 w-1/2 mb-4" />
              <Skeleton className="h-[280px] w-full" />
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DistributionChart metric="duration" data={durationData} />
          <DistributionChart metric="pieces" data={pieceData} />
          <DistributionChart metric="volume" data={volumeData} />
          <DistributionChart metric="malround" data={malroundData} />
        </div>
      )}
    </div>
  );
}
